import { createApi } from '@reduxjs/toolkit/query/react';
import { filteredFilter } from '../../utils/api';
import {properties} from "../../properties.js";
import { authBaseQuery } from '../../app/axiosBaseQuery';
import { PaginationResult, ListRequest } from '../../types/global.d';
import * as Entity from '../types/models/Contractor';
import {createNormalizationMiddleware} from "../../app/normy/normyRtkQuery.ts";

export const CONTRACTOR_API_REDUCER_KEY = 'contractors';
export const CONTRACTOR_PATH = 'contractors';

export const contractorApi = createApi({
    reducerPath: CONTRACTOR_API_REDUCER_KEY,
    baseQuery: authBaseQuery(),
    keepUnusedDataFor: 10,
    refetchOnMountOrArgChange: 10,
    refetchOnFocus: true,
    tagTypes: ['Contractor', 'Contractors'],
    endpoints: (builder) => ({
        getContractorList: builder.query<PaginationResult<Entity.Contractor>, ListRequest>({
            query: (request) => {
                const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request))
                return ({
                    url: `/${ CONTRACTOR_PATH }?${new URLSearchParams(queryParams).toString()}`,
                    method: 'GET',
                })
            },
            providesTags: ['Contractors'],
        }),
        getContractor: builder.query<Entity.Contractor, Entity.PrimaryContractor>({
            queryFn: async (id, { getState }, extraOptions, baseQuery) => {

                const cachedData = contractorNormalizer.getObjectById(id.id);

                if (cachedData) {
                    return { data: cachedData };
                } else {
                    const response = await baseQuery({
                        url: `/${ CONTRACTOR_PATH }/${id.toPath()}`,
                        method: 'GET',
                    });

                    return response;
                }
            },
            providesTags: ['Contractor'],
        }),
        getContractorMultiple: builder.query<Entity.Contractor, Entity.PrimaryContractor[]>({
            query: (ids) => {
                return ({
                    url: `/${ CONTRACTOR_PATH }/ids${Entity.PrimaryContractor.toQueryParams(ids)}`,
                    method: 'GET',
                })
            },
            providesTags: ['Contractors'],
        }),
        getContractorPresentation: builder.query<PaginationResult<Entity.Contractor>, Entity.PresentationContractorRequest>({
            query: (request) => {
              const queryParams: Record<string, any> = Object.assign({}, filteredFilter(request.list))
              return ({
                url: `/${ CONTRACTOR_PATH }/presentation?${new URLSearchParams(queryParams).toString()}`,
                method: 'POST',
                data: request.filter,
              })
            },
            providesTags: ['Contractors'],
        }),
        
        insertContractor: builder.mutation<Entity.Contractor, Entity.CreateContractor>({
            query: (toInsert) => {
                return ({
                    url: `/${ CONTRACTOR_PATH }`,
                    method: 'POST',
                    data: toInsert,
                })
            },
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                const { data: mutationData } = await queryFulfilled;
                dispatch(
                    contractorApi.util.upsertQueryData('getContractor', Entity.PrimaryContractor.fromId(mutationData.id), mutationData),
                );
            },
            invalidatesTags: ['Contractors'],
        }),
        insertMultipleContractor: builder.mutation<Entity.Contractor[], Entity.CreateContractor[]>({
            query: (toInsert) => {
                return ({
                    url: `/${ CONTRACTOR_PATH }/batch`,
                    method: 'POST',
                    data: toInsert,
                })
            },
            onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                const { data: mutationData } = await queryFulfilled;
                for(const item of mutationData) {
                    dispatch(
                            contractorApi.util.upsertQueryData('getContractor', Entity.PrimaryContractor.fromId(item.id), item),
                    );
                }
            },
            invalidatesTags: ['Contractors'],
        }),
        updateContractor: builder.mutation<Entity.Contractor, Entity.UpdateEntityContractor>({
            query: (toUpdate) => {
                return ({
                    url: `/${ CONTRACTOR_PATH }/${toUpdate.id.toPath()}`,
                    method: 'PUT',
                    data: toUpdate.data,
                })
            },
            invalidatesTags: ['Contractors'],
        }),
        updateMultipleContractor: builder.mutation<Entity.Contractor[], Entity.UpdateEntityContractor[]>({
            query: (toUpdate) => {
                return ({
                    url: `/${ CONTRACTOR_PATH }/batch`,
                    method: 'PUT',
                    data: toUpdate,
                })
            },
            invalidatesTags: ['Contractors'],
        }),
        deleteContractor: builder.mutation<Entity.Contractor, Entity.Contractor>({
            query: (toDelete) => {
                return ({
                    url: `/${ CONTRACTOR_PATH }/${Entity.PrimaryContractor.fromEntity(toDelete)?.toPath()}`,
                    method: 'DELETE',
                })
            },
            invalidatesTags: ['Contractors'],
        }),
        deleteMultipleContractor: builder.mutation<Entity.Contractor[], Entity.PrimaryContractor[]>({
            query: (ids) => {
                return ({
                    url: `/${ CONTRACTOR_PATH }/batch${Entity.PrimaryContractor.toQueryParams(ids)}`,
                    method: 'DELETE',
                })
            },
            invalidatesTags: ['Contractors'],
        }),
    }),
})

export const [contractorNormalizer, contractorNormalizerMiddleware] = createNormalizationMiddleware(CONTRACTOR_API_REDUCER_KEY, contractorApi, {
    devLogging: properties.debugLog,
})

export const {
    useGetContractorListQuery,
    useGetContractorMultipleQuery,
    useGetContractorPresentationQuery,
    useGetContractorQuery,
    useInsertContractorMutation,
    useInsertMultipleContractorMutation,
    useUpdateContractorMutation,
    useUpdateMultipleContractorMutation,
    useDeleteContractorMutation,
    useDeleteMultipleContractorMutation,
    
} = contractorApi
